import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Bot, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { tasksAPI } from '../api';

/**
 * AI Processing Indicator Component
 * Shows real-time status of AI task processing with estimated completion time
 */
export const AIProcessingIndicator = ({ taskId, onComplete, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const completedNotifiedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  
  // Keep onComplete ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!taskId) return;
    
    // Reset state when taskId changes
    completedNotifiedRef.current = false;
    setLoading(true);
    setStatus(null);
    
    const fetchStatus = async () => {
      try {
        const response = await tasksAPI.getAIStatus(taskId);
        setStatus(response.data);
        
        // If completed or failed, stop polling and notify ONCE
        if (response.data.processing_status === 'completed' && !completedNotifiedRef.current) {
          completedNotifiedRef.current = true;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onCompleteRef.current?.('completed', response.data);
        } else if (response.data.processing_status === 'failed' && !completedNotifiedRef.current) {
          completedNotifiedRef.current = true;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onCompleteRef.current?.('failed', response.data);
        }
      } catch (error) {
        console.error('Failed to fetch AI status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchStatus();
    
    // Poll every 5 seconds while processing (reduced frequency)
    intervalRef.current = setInterval(fetchStatus, 5000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [taskId]); // Only depend on taskId

  if (loading || !status) {
    return compact ? null : (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading AI status...</span>
      </div>
    );
  }

  if (!status.is_ai_task) {
    return null; // Not an AI task
  }

  const statusConfig = {
    not_queued: {
      icon: Clock,
      color: 'bg-slate-100 text-slate-700 border-slate-200',
      label: 'Queuing...',
    },
    new: {
      icon: Clock,
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      label: 'In Queue',
    },
    in_progress: {
      icon: Loader2,
      color: 'bg-violet-100 text-violet-700 border-violet-200',
      label: 'AI Processing',
      animate: true,
    },
    completed: {
      icon: CheckCircle2,
      color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      label: 'AI Complete',
    },
    failed: {
      icon: AlertCircle,
      color: 'bg-red-100 text-red-700 border-red-200',
      label: 'AI Failed',
    },
  };

  const config = statusConfig[status.processing_status] || statusConfig.not_queued;
  const Icon = config.icon;

  if (compact) {
    return (
      <Badge variant="outline" className={config.color}>
        <Icon className={`h-3 w-3 mr-1 ${config.animate ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3 rounded-lg bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200">
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-violet-600" />
        <span className="font-medium text-violet-900">AI Task Status</span>
        <Badge variant="outline" className={config.color}>
          <Icon className={`h-3 w-3 mr-1 ${config.animate ? 'animate-spin' : ''}`} />
          {config.label}
        </Badge>
      </div>
      
      <p className="text-sm text-violet-700">{status.message}</p>
      
      {status.estimated_time_seconds !== null && status.estimated_time_seconds > 0 && (
        <div className="flex items-center gap-2 text-sm text-violet-600">
          <Clock className="h-3 w-3" />
          <span>Estimated: ~{status.estimated_time_seconds} seconds</span>
        </div>
      )}
      
      {status.deliverable_type && (
        <div className="text-xs text-muted-foreground">
          Output format: {status.deliverable_type === 'document' ? 'Word Document (.docx)' : 
                         status.deliverable_type === 'spreadsheet' ? 'Excel Spreadsheet (.xlsx)' :
                         status.deliverable_type}
        </div>
      )}
      
      {status.exception_flags?.length > 0 && (
        <div className="text-xs text-red-600 mt-1">
          ⚠️ {status.exception_flags[0]}
        </div>
      )}
    </div>
  );
};

/**
 * Compact badge for task cards
 */
export const AITaskBadge = ({ task }) => {
  const [status, setStatus] = useState(null);
  const intervalRef = useRef(null);
  
  useEffect(() => {
    if (task?.assigned_to_type !== 'ai_agent') return;
    
    const fetchStatus = async () => {
      try {
        const response = await tasksAPI.getAIStatus(task.id);
        setStatus(response.data);
        
        // Stop polling if completed or failed
        if (response.data.processing_status === 'completed' || response.data.processing_status === 'failed') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (error) {
        // Silently fail for badge
      }
    };
    
    fetchStatus();
    
    // Poll for active tasks only
    intervalRef.current = setInterval(fetchStatus, 5000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [task?.id, task?.assigned_to_type]);
  
  if (task?.assigned_to_type !== 'ai_agent') {
    return null;
  }
  
  if (!status || status.processing_status === 'not_queued') {
    return (
      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
        <Bot className="h-3 w-3 mr-1" />
        AI Task
      </Badge>
    );
  }
  
  const statusConfig = {
    new: { icon: Clock, color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Queued' },
    in_progress: { icon: Loader2, color: 'bg-violet-100 text-violet-700 border-violet-200', label: 'Processing...', spin: true },
    completed: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Complete' },
    failed: { icon: AlertCircle, color: 'bg-red-100 text-red-700 border-red-200', label: 'Failed' },
  };
  
  const config = statusConfig[status.processing_status] || statusConfig.new;
  const StatusIcon = config.icon;
  
  return (
    <Badge variant="outline" className={config.color}>
      <Bot className="h-3 w-3 mr-1" />
      <StatusIcon className={`h-3 w-3 mr-1 ${config.spin ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

export default AIProcessingIndicator;
